<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    button-location="top"
    @submit="loadCreditCardTable"
  >
    <template #title>
      Credit / Debit Cards
    </template>

    <template #top-form>
      <div
        v-if="!isFamilyDashboard"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Phone Number* </label>

        <phone-number-input
          v-model="phoneNumber"
          class="col-sm-5"
          :page-id="pageId"
          :required="true"
        />

        <div class="col-sm-4 col-12 left-button-group">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading || !isPhoneNumberValid"
          >
            Get All Credit Cards
          </p-button>
        </div>
      </div>

      <message-html
        :result-html="status.errorResultHTML"
        has-bottom-line
      />
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="creditCards"
        :highlight-list="expiredCardsIndexes"
        :table-columns="tableColumns"
      >
        <template #custom-first-column>
          <el-table-column
            v-if="creditCards.length > 0"
            label="Card"
            width="350"
          >
            <template #default="scope">
              <div
                v-if="scope.row"
                :class="{ 'text-red': scope.row.is_expired }"
              >
                <img :src="util.getCardIcon(scope.row.brand)">
                {{ scope.row.name }} **** {{ scope.row.last4 }}
                <span v-if="!scope.row.is_expired"> (Expires {{ scope.row.expiration }}) </span>
                <span
                  v-if="scope.row.is_expired"
                  class="expired-indicator"
                >
                  EXPIRED
                </span>
                <span
                  v-if="defaultCreditCard?.id === scope.row.id"
                  class="default-indicator"
                >
                  DEFAULT
                </span>
              </div>
            </template>
          </el-table-column>
        </template>

        <template #right-fixed-columns>
          <el-table-column
            v-if="creditCards.length > 0"
            fixed="right"
            label="Actions"
            width="230"
          >
            <template #default="scope">
              <div class="actions card-info">
                <a @click.prevent="showCreditCardUpdateDialog(scope.$index)"> Update </a>
                <a
                  v-if="defaultCreditCard?.id !== scope.row.id"
                  @click.prevent="setDefaultCreditCardAction(scope.$index)"
                >
                  Make Default
                </a>
                <a @click.prevent="removeCreditCard(scope.$index)"> Delete </a>
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>

    <update-credit-card-info-dialog
      v-model="selectedCreditCard.cardInfo"
      :is-show-dialog="status.isShowCreditCardUpdateDialog"
      @submit="updateCreditCardInfo"
      @cancel="status.isShowCreditCardUpdateDialog = false"
    />
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { cloneDeep } from 'lodash';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { ExtendedCardType } from '@/models/payment';
import { TableColumnType, HighlightTableItemType } from '@/models/table';
import { paymentService } from '@/services/payment';
import { callerService } from '@/services/caller';

const UpdateCreditCardInfoDialog = defineAsyncComponent(() => import('../../Dialogs/UpdateCreditCardInfo.vue') as any);

const pageId = 'credit-card-list';
const tableColumns: TableColumnType[] = [
  { field: 'email', label: 'Email', width: 200 },
  { field: 'phone', label: 'Phone', width: 120 },
  { field: 'billingAddress', label: 'Address', width: 250 },
  { field: 'cardAdderInfo', label: 'Created By', width: 200 },
  { field: 'createdAt', label: 'Date Created', width: 120 },
  { field: 'createdTime', label: 'Time Created', width: 120 },
];
const expiredCardsIndexes: Ref<HighlightTableItemType[]> = ref([]);

const { customerInfo, customerPhoneNumber } = useCustomer();
const { isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialogWithValidation } = useSwalDialog();
const { partnerId } = usePartner();

const phoneNumber = ref('');
const callerId = ref('');
const creditCards: Ref<ExtendedCardType[]> = ref([]);
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  errorResultHTML: '',
  isShowCreditCardUpdateDialog: false,
});
const selectedCreditCard = reactive({
  index: -1,
  cardInfo: {} as any,
});

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));
const defaultCreditCard = computed(() => (creditCards.value?.length ? creditCards.value.find((card) => card.is_default) : null));

const isUserLoadedOnOperatorsRightPanel = computed(() => {
  if (isFamilyDashboard.value) {
    return false;
  }

  return customerInfo.value?.id === callerId.value;
});

const loadCreditCardTable = async () => {
  status.spinnerMessage = 'Loading Caller Info...';
  status.errorResultHTML = '';

  status.isLoading = true;
  try {
    const response = await callerService.fetchCustomerInfo({
      phoneNumber: phoneNumber.value,
      partnerId: isPartnerDashboard.value ? partnerId.value : null,
    });

    callerId.value = response.id;
    await getCreditCards();
  } catch (error) {
    creditCards.value = [];
    status.errorResultHTML = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const getCreditCards = async () => {
  status.spinnerMessage = 'Loading Credit Cards...';
  status.errorResultHTML = '';

  status.isLoading = true;
  expiredCardsIndexes.value = [];
  try {
    creditCards.value = await paymentService.fetchPaymentSources({
      userId: callerId.value,
    });

    creditCards.value.forEach((card: ExtendedCardType, index: number) => {
      if (card.is_expired) {
        expiredCardsIndexes.value.push({ index, class: 'disabled-table-row', type: 'row' });
      }
    });
  } catch (error) {
    creditCards.value = [];
    status.errorResultHTML = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const removeCreditCard = async (index: number) => {
  const notes = await showConfirmPopupDialogWithValidation({
    text: `Are you sure you want to remove the card (${creditCards.value[index].last4})?`,
    title: 'Remove Credit Card?',
    input: 'text',
    inputPlaceholder: 'Please input reason',
    validationText: 'Please input reason',
    confirmButtonText: 'Yes, Remove',
  });

  if (!notes) {
    return;
  }

  status.spinnerMessage = 'Deleting Card...';
  status.isLoading = true;
  try {
    await paymentService.deletePaymentSource({
      userId: callerId.value,
      cardId: creditCards.value[index].id,
      notes,
    });

    creditCards.value.splice(index, 1);

    showSuccessPopupDialog({
      text: 'The credit card has been deleted successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const setDefaultCreditCardAction = async (index: number) => {
  status.spinnerMessage = 'Selecting Default Card...';
  status.isLoading = true;
  try {
    await setDefaultCreditCard(index);

    showSuccessPopupDialog({
      text: 'The default credit card has been selected successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const setDefaultCreditCard = async (index: number) => {
  await paymentService.makeDefaultCard({
    userId: callerId.value,
    cardId: creditCards.value[index].id,
  });

  util.moveItem(creditCards.value, index, 0);

  if (isUserLoadedOnOperatorsRightPanel.value) {
    events.emit('refresh-right-panel');
  }
};

const showCreditCardUpdateDialog = (index: number) => {
  selectedCreditCard.cardInfo = cloneDeep(creditCards.value[index]);
  selectedCreditCard.index = index;
  status.isShowCreditCardUpdateDialog = true;
};

const updateCreditCardInfo = async () => {
  status.isShowCreditCardUpdateDialog = false;

  status.spinnerMessage = 'Updating Credit Card...';
  status.isLoading = true;
  try {
    const response = await paymentService.updatePaymentSource({
      data: {
        name: selectedCreditCard.cardInfo.name,
        exp_month: Number(selectedCreditCard.cardInfo.exp_month),
        exp_year: Number(selectedCreditCard.cardInfo.exp_year),
        phone: util.getValidPhoneNumber(selectedCreditCard.cardInfo.phone),
        email: selectedCreditCard.cardInfo.email,
        address_line1: selectedCreditCard.cardInfo.address_line1,
        address_line2: selectedCreditCard.cardInfo.address_line2,
        address_city: selectedCreditCard.cardInfo.address_city,
        address_state: selectedCreditCard.cardInfo.address_state,
        address_zip: selectedCreditCard.cardInfo.address_zip,
        address_country: selectedCreditCard.cardInfo.address_country,
        isDirectExpress: selectedCreditCard.cardInfo.isDirectExpress,
      },
      cardId: selectedCreditCard.cardInfo.id,
      userId: callerId.value,
    });

    // update credit card in the table with the newly added card info
    creditCards.value.splice(selectedCreditCard.index, 1, response);

    // if it was the default credit card that was updated,
    // update this.defaultCreditCard reference to the updated credit card
    const wasDefaultCreditCardUpdated = defaultCreditCard.value?.id === response.id;
    if (wasDefaultCreditCardUpdated && isUserLoadedOnOperatorsRightPanel.value) {
      events.emit('refresh-right-panel');
    }

    selectedCreditCard.index = -1;

    showSuccessPopupDialog({
      text: 'The credit card info has been updated successfully!',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

watch(customerInfo, (value: any, oldValue: any) => {
  phoneNumber.value = customerPhoneNumber.value;
  if (!phoneNumber.value) {
    creditCards.value = [];
  }

  if (!value.id) {
    return;
  }

  // reload credit card only one time for same customer
  const isDifferentUser = value.id !== oldValue.id;
  if (isDifferentUser) {
    callerId.value = value.id;
    getCreditCards();
  }
});

onMounted(() => {
  phoneNumber.value = customerPhoneNumber.value;

  // load credit card list if customer exists
  if (customerInfo.value?.id) {
    callerId.value = customerInfo.value.id;
    getCreditCards();
  }

  events.on('reload-credit-cards', getCreditCards);
});

onBeforeUnmount(() => {
  events.off('reload-credit-cards', getCreditCards);
});
</script>

<style lang="scss">
.credit-card-list {
  .table-view {
    div.card-info {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
    }
    img {
      width: 30px;
      margin-right: 5px;
    }
    .default-indicator {
      background-color: lightgreen;
    }
    .expired-indicator {
      background-color: #f90000;
      color: white !important;
    }
    a {
      color: #008b8b !important;
      &:hover {
        color: #dc22b1 !important;
      }
    }
  }
}
</style>
